import { useState } from 'react';
import { withAuthenticationRequired, useAuth0 } from '@auth0/auth0-react';
import { useUserStatus } from '../context/UserStatusContext';
import { loadStripe } from '@stripe/stripe-js';
import { createCheckoutSession } from '../services/apiService';
import styles from './PricingPage.module.css';

// --- Stripe Price IDs from your Dashboard ---
const PRICE_IDS = {
  premium: {
    monthly: 'price_PREMIUM_MONTHLY_ID_HERE',
    annual: 'price_PREMIUM_ANNUAL_ID_HERE',
  },
  pro: {
    monthly: 'price_PRO_MONTHLY_ID_HERE',
    annual: 'price_PRO_ANNUAL_ID_HERE',
  },
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CURRENT_FEATURES = [
  { key: 'verification', text: 'Psychometric Verification' },
  { key: 'profiles', text: 'Public Profiles' },
  { key: 'personas', text: 'Creative Personas' },
  { key: 'credits', text: 'Ego Credits (⚡️)' },
  { key: 'engagement', text: 'Engagement (🔥)' },
  { key: 'hdImages', text: 'HD Image Gen. (🎨)' },
];

const UPCOMING_FEATURES = [
  { key: 'branding', text: 'Custom Branding & Colors' },
  { key: 'analytics-basic', text: 'Audience Analytics (Basic)' },
  { key: 'analytics-advanced', text: 'Audience Analytics (Advanced)' },
  { key: 'leadCapture', text: 'Lead Capture & Email Alerts' },
  { key: 'rag', text: 'Document Upload (RAG)' },
  { key: 'chatbotEmbeddings', text: 'Chatbot Embeddings' },
  { key: 'api', text: 'Developer API Access' },
];

const personalTiers = [
  {
    name: 'Free',
    planId: 'free',
    price: { monthly: '$0', annual: '-' },
    subtitle: 'Establish your core digital identity.',
    features: {
      profiles: '1',
      personas: '3',
      engagement: '15 / mo',
      credits: '50 / mo',
      verification: 'Basic',
    },
    comingSoon: [],
  },
  {
    name: 'Premium',
    planId: 'premium',
    price: { monthly: '$12.99', annual: '$129' },
    pricePer: { monthly: '/mo', annual: '/yr' },
    subtitle: 'For creators building their digital presence.',
    features: {
      verification: 'Basic',
      profiles: '5',
      personas: '10',
      engagement: '100 / mo',
      credits: '200 / mo',
      hdImages: '5 / mo',
    },
    comingSoon: ['branding', 'analytics-basic'],
    recommended: true,
  },
  {
    name: 'Pro',
    planId: 'pro',
    price: { monthly: '$49.99', annual: '$499' },
    pricePer: { monthly: '/mo', annual: '/yr' },
    subtitle: 'For professionals building an automated brand.',
    features: {
      verification: 'Full',
      profiles: '10',
      personas: '100',
      engagement: '1,000 / mo',
      credits: '1,000 / mo',
      hdImages: '20 / mo',
    },
    comingSoon: ['branding', 'analytics-advanced', 'leadCapture', 'rag'],
  },
];

const businessTiers = [
  {
    name: 'Starter',
    planId: 'business-starter',
    price: { monthly: '$69.99', annual: '$699' },
    pricePer: { monthly: '/mo', annual: '/yr' },
    subtitle: 'For small businesses and teams getting started.',
    features: {
      verification: 'Full',
      profiles: '25',
      personas: '250',
      engagement: '2,500 / mo',
      credits: '5,000 / mo',
      hdImages: '50 / mo',
    },
    comingSoon: [
      'branding',
      'leadCapture',
      'rag',
      'chatbotEmbeddings',
      'analytics-advanced',
    ],
  },
  {
    name: 'Growth',
    planId: 'business-growth',
    price: { monthly: '$129.99', annual: '$1,299' },
    pricePer: { monthly: '/mo', annual: '/yr' },
    subtitle: 'For companies scaling their AI interactions.',
    features: {
      verification: 'Full',
      profiles: 'Unlimited',
      personas: 'Unlimited',
      engagement: '10,000 / mo',
      credits: '15,000 / mo',
      hdImages: '100 / mo',
    },
    comingSoon: [
      'branding',
      'analytics-advanced',
      'leadCapture',
      'rag',
      'chatbotEmbeddings',
      'api',
    ],
    recommended: true,
  },
  {
    name: 'Enterprise',
    planId: 'enterprise',
    price: { monthly: 'Custom', annual: 'Custom' },
    pricePer: { monthly: '', annual: '' },
    subtitle: 'For large-scale deployment with dedicated support.',
    features: {
      verification: 'Full',
      profiles: 'Unlimited',
      personas: 'Unlimited',
      engagement: 'Custom Quotas',
      credits: 'Custom Quotas',
      hdImages: 'Custom Quotas',
      rag: 'Unlimited',
      api: 'Full Access',
      // The chatbotEmbeddings feature is fully included here
      chatbotEmbeddings: true,
    },
    comingSoon: [],
  },
];

function PricingPage() {
  const { userStatus } = useUserStatus();
  const { getAccessTokenSilently } = useAuth0();
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'
  const [activePlan, setActivePlan] = useState('personal');

  const handleUpgradeClick = async (planId) => {
    try {
      const accessToken = await getAccessTokenSilently();
      const priceId = PRICE_IDS[planId][billingCycle];

      const { sessionId } = await createCheckoutSession(
        {
          priceId: priceId,
          successUrl: `${window.location.origin}/passport?upgraded=true`,
          cancelUrl: window.location.href,
          // Passing email is a best practice for linking Stripe customers to your user DB
          customerEmail: userStatus?.email,
        },
        accessToken
      );

      if (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY.includes('pk_live')) {
        const stripe = await stripePromise;
        await stripe.redirectToCheckout({ sessionId });
      } else {
        console.log('Stripe is in test/mock mode. Redirect prevented.');
        alert(`(Mock Mode) Would redirect to Stripe for session: ${sessionId}`);
      }
    } catch (error) {
      console.error('Failed to create Stripe checkout session:', error);
    }
  };

  const tiersToDisplay =
    activePlan === 'personal' ? personalTiers : businessTiers;

  const renderButton = (tier) => {
    if (userStatus?.planTier === tier.planId) {
      return (
        <button className="action-button secondary" disabled>
          Your Current Plan
        </button>
      );
    }
    if (tier.planId === 'free') {
      return (
        <button className="action-button secondary" disabled>
          Included
        </button>
      );
    }
    return (
      <button
        onClick={() => handleUpgradeClick(tier.planId)}
        className={
          tier.recommended ? 'action-button primary' : 'action-button secondary'
        }
      >
        {userStatus?.planTier === 'free' ? 'Upgrade' : 'Change Plan'}
      </button>
    );
  };

  return (
    <div>
      <div className="page-header">
        <h2>Plans for Every Vision</h2>
      </div>
      <p className={styles.introText}>
        Start for free and scale as you grow. Our mission is to provide the
        tools for building a more authentic and interactive digital world.
      </p>

      <div className={styles.planTypeToggle}>
        <button
          onClick={() => setActivePlan('personal')}
          className={activePlan === 'personal' ? styles.active : ''}
        >
          Personal Plans
        </button>
        <button
          onClick={() => setActivePlan('business')}
          className={activePlan === 'business' ? styles.active : ''}
        >
          Business Plans
        </button>
      </div>

      {activePlan === 'personal' && (
        <div className={styles.billingContainer}>
          <div className={styles.billingToggle}>
            <button
              onClick={() => setBillingCycle('monthly')}
              className={billingCycle === 'monthly' ? styles.active : ''}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={billingCycle === 'annual' ? styles.active : ''}
            >
              Yearly
            </button>
          </div>
          <p className={styles.billingSubtitle}>
            Pay monthly for flexibility, or choose a yearly plan to save.
          </p>
        </div>
      )}

      <div className={styles.pricingGrid}>
        {tiersToDisplay.map((tier) => (
          <div
            key={tier.name}
            className={`${styles.tierCard} ${tier.recommended ? styles.recommended : ''}`}
          >
            {tier.recommended && (
              <div className={styles.recommendedBadge}>Best Value</div>
            )}
            <h3>{tier.name}</h3>
            <p className={styles.price}>
              {tier.price[billingCycle]}
              {tier.pricePer && (
                <span className={styles.pricePer}>
                  {tier.pricePer[billingCycle]}
                </span>
              )}
            </p>
            <p className={styles.tierSubtitle}>{tier.subtitle}</p>

            <ul className={styles.featureList}>
              {CURRENT_FEATURES.map((feature) => {
                const featureValue = tier.features[feature.key];
                const isIncluded = !!featureValue;
                const valueText =
                  typeof featureValue === 'string' ? `: ${featureValue}` : '';
                return (
                  <li
                    key={feature.key}
                    className={isIncluded ? styles.included : styles.excluded}
                  >
                    <span className={styles.checkIcon}>
                      {isIncluded ? '✔' : '✖'}
                    </span>
                    {feature.text}
                    {valueText}
                  </li>
                );
              })}
            </ul>

            {tier.comingSoon?.length > 0 && (
              <>
                <h4 className={styles.comingSoonHeader}>Coming Soon:</h4>
                <ul className={styles.comingSoonList}>
                  {UPCOMING_FEATURES.map((feature) => {
                    const isIncluded = tier.comingSoon.includes(feature.key);
                    if (!isIncluded) return null;
                    return (
                      <li key={feature.key}>
                        <i className="fas fa-wrench"></i> {feature.text}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            <div className={styles.buttonWrapper}>{renderButton(tier)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default withAuthenticationRequired(PricingPage);
